<template>
  <div>
    <div v-if="questionsToRender.length > 0" class="inner t-center">
      <div class="t-title sm mb-l header">Help us improve!</div>
      <div class="t-title sm mb-l text">
        Thanks for using Singapore Trip Planner. Please take a moment to let us know what you thought of your
        experience.
      </div>
      <div class="questions-wrapper">
        <div v-for="question in questionsToRender" :key="question.id">
          <component :is="question.component" v-bind="question.props" @input="question.events.onChange" />
        </div>
      </div>
      <AmexButton
        class="btn amex-primary"
        :class="{disabled: !isSurveyCompleted}"
        @click="onSubmitClick"
        :disabled="!isSurveyCompleted"
        >Submit</AmexButton
      >
    </div>
    <p v-else>Loading...</p>
  </div>
</template>

<script>
import {AmexButton} from '@mobi-components/amex-bluesky-components';
import RatingQuestion from './rating-question.vue';
import TextQuestion from './text-question.vue';
import EnumQuestion from './enum-question.vue';
import {getUserId} from '@/services/auth';
import {getSurvey, saveSurvey, SurveyQuestionCategory} from './get-survey';

export default {
  components: {
    AmexButton,
  },
  name: 'TimeFilter',
  data() {
    return {
      overrideQuestions: [],
      survey: null,
    };
  },
  props: {
    questions: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },

  mounted() {
    this.fetchSurveys();
  },

  computed: {
    isSurveyCompleted() {
      return this.questionsToRender.every((x) => {
        return Boolean(this.value[x.id]) || x.optional;
      });
    },
    questionsToRender() {
      return this.overrideQuestions
        .map((x) => {
          switch (x.category) {
            case SurveyQuestionCategory.RATING:
              return {
                id: x.id,
                optional: x.optional,
                component: RatingQuestion,
                props: {
                  value: this.value[x.id],
                  question: x.description,
                },
                events: {
                  onChange: (value) => this.$emit('change', {...this.value, [x.id]: value}),
                },
              };
            case SurveyQuestionCategory.TEXT:
              return {
                id: x.id,
                optional: x.optional,
                component: TextQuestion,
                props: {
                  value: this.value[x.id],
                  question: x.description,
                },
                events: {
                  onChange: (value) => {
                    this.$emit('change', {...this.value, [x.id]: value.target.value});
                  },
                },
              };
            case SurveyQuestionCategory.OPTION:
              return {
                id: x.id,
                optional: x.optional,
                component: EnumQuestion,
                props: {
                  value: this.value[x.id],
                  question: x.description,
                  options: x.answers,
                  active: this.value[x.id],
                },
                events: {
                  onChange: (value) => this.$emit('change', {...this.value, [x.id]: value}),
                },
              };
          }
        })
        .filter(Boolean);
    },
  },

  methods: {
    async fetchSurveys() {
      const userId = getUserId();
      const surveys = await getSurvey(userId);
      if (surveys.length) {
        this.survey = surveys[0];
        this.overrideQuestions = this.survey.questions;
      } else {
        this.survey = null;
      }
    },
    onSubmitClick() {
      this.$emit('close');
      if (!this.question) {
        const userId = getUserId();
        const surveyId = this.survey?.id;
        const answers = Object.keys(this.value)
          .map((questionId) => {
            const question = this.overrideQuestions.find((x) => x.id === questionId);
            switch (question.category) {
              case SurveyQuestionCategory.RATING:
                return question.answers[this.value[questionId] - 1];
              case SurveyQuestionCategory.OPTION:
                return question.answers.find((x) => x.id === this.value[questionId]);
              case SurveyQuestionCategory.TEXT:
                return question.answers.length ? {...question.answers[0], description: this.value[questionId]} : null;
              default:
                return null;
            }
          })
          .filter(Boolean);
        return saveSurvey(userId, surveyId, answers).catch(() => null);
      }
    },
  },
};
</script>

<style scoped>
.header {
  margin-bottom: 10px;
}
.text {
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 140%;
  color: #333333;
  text-align: center;
  max-width: 440px;
  margin-left: auto;
  margin-right: auto;
}
.btn {
  margin-left: auto;
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  margin: 15px auto 5px;
}

.btn.amex-primary {
  background: var(--btn-primary-color, hsl(208, 100%, 41%));
  border-radius: 10px;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 118.14%;
  text-align: center;
  letter-spacing: -0.005em;

  color: #ffffff;
  padding: 12px 100px 10px;
}

.btn.amex-primary:hover {
  background: var(--btn-primary-color-active, hsl(214, 64%, 30%));
}

.amex-primary.disabled {
  pointer-events: none;
  cursor: not-allowed;
  background: #e8e8e8;
  color: #6e6e6e;
}

.questions-wrapper {
  overflow: auto;
}
</style>
